import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMe } from '../organisation/useMe';
import { caseInternalApi, caseRationaleInternalApi, caseStatusInternalApi } from '../apis';
import { InternalApiCase, InternalApiCreateCaseRequest, InternalApiUpdateCaseDetailsRequest } from '../api-internal';
import { convertToDropdownOption } from '../common/helper';
import { CASES_QUERY_KEY } from './casesQueryActions';

export const CASE_QUERY_KEY = 'case';
export const CASE_RATIONALES_QUERY_KEY = 'caseRationales';
export const CASE_STATUSES_QUERY_KEY = 'caseStatuses';

const getCaseQueryKey = ({ handle, organisationId }: { handle: string; organisationId: number | undefined }) => [
  CASE_QUERY_KEY,
  handle,
  organisationId,
];

export const useQueryCaseRationaleOptions = () => {
  const { currentOrganisation } = useMe();
  return useQuery({
    queryKey: [CASE_RATIONALES_QUERY_KEY, currentOrganisation?.id],
    queryFn: async () => await caseRationaleInternalApi.findAllCaseRationales(),
    select: (rationales) => rationales.map((rationale) => convertToDropdownOption(rationale.textName, rationale.code)),
    enabled: !!currentOrganisation?.id,
    staleTime: 60_000,
  });
};

export const useQueryCaseStatusOptions = () => {
  const { currentOrganisation } = useMe();
  return useQuery({
    queryKey: [CASE_STATUSES_QUERY_KEY, currentOrganisation?.id],
    queryFn: async () => await caseStatusInternalApi.findAllCaseStatuses(),
    select: (statuses) => statuses.map((status) => convertToDropdownOption(status.textName, status.code)),
    enabled: !!currentOrganisation?.id,
    staleTime: 60_000,
  });
};

export const useQueryCase = (handle: string) => {
  const { currentOrganisation } = useMe();

  return useQuery({
    queryKey: getCaseQueryKey({ handle, organisationId: currentOrganisation?.id }),
    queryFn: async () => await caseInternalApi.findCaseByHandle({ handle }),
    enabled: !!currentOrganisation?.id,
  });
};

export const useCreateCase = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (internalApiCreateCaseRequest: InternalApiCreateCaseRequest) => {
      return caseInternalApi.createCase({ internalApiCreateCaseRequest });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CASES_QUERY_KEY, currentOrganisation?.id] });
    },
  });
};

export const useUpdateCase = (handle: string) => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();
  const caseQueryKey = getCaseQueryKey({ handle, organisationId: currentOrganisation?.id });

  return useMutation({
    mutationFn: ({
      internalApiUpdateCaseDetailsRequest,
    }: {
      internalApiUpdateCaseDetailsRequest: InternalApiUpdateCaseDetailsRequest;
    }) => {
      return caseInternalApi.updateCaseDetails({ handle, internalApiUpdateCaseDetailsRequest });
    },
    onSuccess: (newCaseDetails) => {
      const oldCase = queryClient.getQueryData<InternalApiCase>(caseQueryKey);
      queryClient.setQueryData(caseQueryKey, { ...oldCase, details: newCaseDetails });
      queryClient.invalidateQueries({ queryKey: [CASES_QUERY_KEY, currentOrganisation?.id] });
    },
  });
};

export const useArchiveCase = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: (handle: string) => {
      return caseInternalApi.archiveCase({ handle });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [CASES_QUERY_KEY, currentOrganisation?.id] });
    },
  });
};
